import { useMutation } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  EXPORT_ACTIVITIES,
  EXPORT_ACTIVITIES_MASS,
} from '@/modules/exports/mutations'
import { useNavigate } from 'react-router'
import { useOnErrorAuto } from '@/utils/hooks'
import {
  TOGGLE_APPROVED_DRIVER_ACTIVITY_MUTATION,
  UPDATE_OR_CREATE_DRIVER_DIET_MUTATION,
  UPDATE_OR_CREATE_SALARY_DAY_MUTATION,
} from './mutations'

export function useActivityExportMutations(onCompleted?: () => void) {
  const translations = useTranslate({
    hours: 'common.hours',
    diet: 'common.diet',
    summary: 'activities.work-summary',
    error: 'error.generic-server-error',
    downloadSuccess: 'exports.success-user-exports',
    exportTimeout: 'exports.export-timeout',
    mailSuccess: 'exports.success-email-process',
  })

  const navigate = useNavigate()

  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const [exportActivitiesMass, { loading: mailMassActivityLoading }] =
    useMutation(EXPORT_ACTIVITIES_MASS, {
      onCompleted(data) {
        if (data?.exportActivitiesMass.exportTimeout) {
          addToast('info', translations.exportTimeout)
        } else if (data?.exportActivitiesMass.html) {
          void navigate('/export', {
            state: { html: data?.exportActivitiesMass.html },
          })
        } else if (data?.exportActivitiesMass.ok) {
          addToast('success', translations.downloadSuccess)
        } else addToast('error', translations.error)

        onCompleted?.()
      },
      onError: onErrorAuto(translations.error),
    })

  const [exportActivities, { loading: mailActivityLoading }] = useMutation(
    EXPORT_ACTIVITIES,
    {
      onCompleted(data) {
        if (data?.exportActivities.exportTimeout) {
          addToast('info', translations.exportTimeout)
        } else if (data?.exportActivities.html) {
          void navigate('/export', {
            state: { html: data?.exportActivities.html },
          })
        } else if (data?.exportActivities.ok) {
          addToast('success', translations.downloadSuccess)
        } else {
          addToast('error', translations.error)
        }
        onCompleted?.()
      },
    }
  )

  return {
    exportActivitiesMass,
    exportActivities,
    loading: mailMassActivityLoading || mailActivityLoading,
  }
}

export function useActivityMutations() {
  const translations = useTranslate({
    hours: 'common.hours',
    diet: 'common.diet',
    summary: 'activities.work-summary',
    toast: {
      error: 'error.generic-server-error',
      fetch: {
        success: 'activities.starting-activity-fetching-process',
      },
      approve: {
        success: 'activities.success.approve-activities',
      },
    },
  })
  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const [toggleActivityMutation, { loading: toggleLoading }] = useMutation(
    TOGGLE_APPROVED_DRIVER_ACTIVITY_MUTATION,
    {
      refetchQueries: [
        'DriverActivityPeriodReport',
        'DriverActivityPeriodReportShallow',
        'AllDynamicColumnsQuery',
        'DriverActivityDayReport',
      ],
      onCompleted(data) {
        if (data?.toggleApprovedDriverActivity.ok)
          addToast('success', translations.toast.approve.success)
        else addToast('error', translations.toast.error)
      },
      onError: onErrorAuto(translations.toast.error),
    }
  )

  const loading = toggleLoading

  return {
    loading,

    toggleApproved: toggleActivityMutation,
  }
}

export function useDietDayMutations() {
  const translations = useTranslate({
    toast: {
      error: 'error.generic-server-error',
      success: 'activities.updated-diet-successfully',
    },
  })
  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const [updateOrCreateDriverDiet] = useMutation(
    UPDATE_OR_CREATE_DRIVER_DIET_MUTATION,
    {
      refetchQueries: [
        'DriverActivityPeriodReport',
        'DriverActivityPeriodReportShallow',
        'AllDynamicColumnsQuery',
        'DriverActivityDayReport',
      ],
      update(cache, { data }) {
        const diet = data?.updateOrCreateDriverDiet.diet
        if (!diet) return

        cache.modify({
          id: cache.identify(diet),
          fields: {
            salaryCode: () => diet.salaryCode,
            partOfDay: () => diet.partOfDay,
          },
        })
        if (diet.calendarItem) {
          cache.modify({
            id: cache.identify(diet.calendarItem),
            fields: {
              driverDiet: () => diet,
            },
          })
        }
      },
      onCompleted(data) {
        if (data?.updateOrCreateDriverDiet.ok)
          addToast('success', translations.toast.success)
        else addToast('error', translations.toast.error)
      },
      onError: onErrorAuto(translations.toast.error),
    }
  )
  return {
    updateOrCreateDriverDiet,
  }
}

export function useSalaryDayMutations() {
  const translations = useTranslate({
    summary: 'activities.work-summary',
    toast: {
      error: 'error.generic-server-error',
      success: 'activities.updated-salary-day-successfully',
    },
  })
  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const [updateOrCreateSalaryDay] = useMutation(
    UPDATE_OR_CREATE_SALARY_DAY_MUTATION,
    {
      refetchQueries: [
        'DriverActivityPeriodReport',
        'DriverActivityPeriodReportShallow',
        'AllDynamicColumnsQuery',
        'DriverActivityDayReport',
      ],
      update(cache, { data }) {
        const salaryDay = data?.updateOrCreateSalaryDay.salaryDay
        if (!salaryDay) return

        cache.modify({
          id: cache.identify(salaryDay),
          fields: {
            salaryCode: () => salaryDay.salaryCode,
            partOfDay: () => salaryDay.partOfDay,
          },
        })
      },
      onCompleted(data) {
        if (data?.updateOrCreateSalaryDay.ok)
          addToast('success', translations.toast.success)
        else addToast('error', translations.toast.error)
      },
      onError: onErrorAuto(translations.toast.error),
    }
  )
  return {
    updateOrCreateSalaryDay,
  }
}
